import {
	Activity,
	Archive,
	ChevronRight,
	ExternalLink,
	Eye,
	EyeOff,
	Filter,
	Fingerprint,
	FolderInput,
	HelpCircle,
	Inbox,
	Key,
	Plus,
	RefreshCw,
	Settings,
	Shield,
	Tag,
	Users,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LinkSummaryResponse, QueueResponse } from '@/api/client';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/contexts/auth';
import { useModalManager } from '@/hooks/useModalManager';
import { cn } from '@/lib/utils';

interface Command {
	id: string;
	label: string;
	shortcut?: string;
	group: 'navigation' | 'actions' | 'view' | 'settings';
	icon: React.ReactNode;
	action: () => void;
	disabled?: boolean;
}

interface CommandPaletteProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	queues: QueueResponse[];
	selectedLink: LinkSummaryResponse | null;
	showNsfw: boolean;
	onNavigateToQueue: (queueId: number) => void;
	onToggleNsfw: () => void;
	onArchiveSelected: () => void;
	onOpenQuickAdd: () => void;
	onOpenCategorySelector: () => void;
	onOpenOriginalUrl: () => void;
	onEnrichSelected: () => void;
	onShowShortcuts: () => void;
}

// Simple fuzzy match - checks if all characters in query appear in order in target
function fuzzyMatch(query: string, target: string): boolean {
	const q = query.toLowerCase();
	const t = target.toLowerCase();
	let qi = 0;
	for (let ti = 0; ti < t.length && qi < q.length; ti++) {
		if (t[ti] === q[qi]) qi++;
	}
	return qi === q.length;
}

export function CommandPalette({
	open,
	onOpenChange,
	queues,
	selectedLink,
	showNsfw,
	onNavigateToQueue,
	onToggleNsfw,
	onArchiveSelected,
	onOpenQuickAdd,
	onOpenCategorySelector,
	onOpenOriginalUrl,
	onEnrichSelected,
	onShowShortcuts,
}: CommandPaletteProps) {
	const { user } = useAuth();
	const { openModal } = useModalManager();
	const [query, setQuery] = useState('');
	const [selectedIndex, setSelectedIndex] = useState(0);
	const selectedRef = useRef<HTMLButtonElement | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	// Build commands list
	const commands = useMemo<Command[]>(() => {
		const cmds: Command[] = [];

		// Navigation commands
		cmds.push({
			id: 'nav-inbox',
			label: 'Go to Inbox',
			group: 'navigation',
			icon: <Inbox className="size-4" />,
			action: () => {
				onNavigateToQueue(0);
				onOpenChange(false);
			},
		});

		for (const queue of queues) {
			cmds.push({
				id: `nav-queue-${queue.id}`,
				label: `Go to ${queue.name}`,
				group: 'navigation',
				icon: <div className="size-3 rounded-full" style={{ backgroundColor: queue.color }} />,
				action: () => {
					onNavigateToQueue(queue.id);
					onOpenChange(false);
				},
			});
		}

		cmds.push({
			id: 'nav-archive',
			label: 'Go to Archive',
			group: 'navigation',
			icon: <Archive className="size-4" />,
			action: () => {
				onNavigateToQueue(-1); // ARCHIVE_QUEUE_ID
				onOpenChange(false);
			},
		});

		// Action commands
		cmds.push({
			id: 'action-add-link',
			label: 'Add New Link',
			shortcut: '⌘N',
			group: 'actions',
			icon: <Plus className="size-4" />,
			action: () => {
				onOpenChange(false);
				onOpenQuickAdd();
			},
		});

		cmds.push({
			id: 'action-archive',
			label: 'Archive Selected',
			shortcut: 'E',
			group: 'actions',
			icon: <Archive className="size-4" />,
			action: () => {
				onArchiveSelected();
				onOpenChange(false);
			},
			disabled: !selectedLink,
		});

		cmds.push({
			id: 'action-category',
			label: 'Change Category',
			shortcut: 'L',
			group: 'actions',
			icon: <Tag className="size-4" />,
			action: () => {
				onOpenChange(false);
				onOpenCategorySelector();
			},
			disabled: !selectedLink,
		});

		cmds.push({
			id: 'action-move-queue',
			label: 'Move to Queue',
			shortcut: 'Q',
			group: 'actions',
			icon: <FolderInput className="size-4" />,
			action: () => {
				onOpenChange(false);
				// This will trigger the queue selector in App.tsx
				document.dispatchEvent(new CustomEvent('open-queue-selector'));
			},
			disabled: !selectedLink,
		});

		cmds.push({
			id: 'action-open-url',
			label: 'Open Original URL',
			shortcut: 'O',
			group: 'actions',
			icon: <ExternalLink className="size-4" />,
			action: () => {
				onOpenOriginalUrl();
				onOpenChange(false);
			},
			disabled: !selectedLink,
		});

		cmds.push({
			id: 'action-enrich',
			label: 'Re-enrich Link',
			group: 'actions',
			icon: <RefreshCw className="size-4" />,
			action: () => {
				onEnrichSelected();
				onOpenChange(false);
			},
			disabled: !selectedLink,
		});

		// View commands
		cmds.push({
			id: 'view-nsfw',
			label: showNsfw ? 'Hide NSFW Content' : 'Show NSFW Content',
			group: 'view',
			icon: showNsfw ? <EyeOff className="size-4" /> : <Eye className="size-4" />,
			action: () => {
				onToggleNsfw();
				onOpenChange(false);
			},
		});

		cmds.push({
			id: 'view-shortcuts',
			label: 'Show Keyboard Shortcuts',
			shortcut: '?',
			group: 'view',
			icon: <HelpCircle className="size-4" />,
			action: () => {
				onOpenChange(false);
				onShowShortcuts();
			},
		});

		// Settings commands (based on permissions)
		cmds.push({
			id: 'settings-security',
			label: 'Security Settings',
			group: 'settings',
			icon: <Shield className="size-4" />,
			action: () => {
				onOpenChange(false);
				openModal('security');
			},
		});

		cmds.push({
			id: 'settings-passkeys',
			label: 'Manage Passkeys',
			group: 'settings',
			icon: <Fingerprint className="size-4" />,
			action: () => {
				onOpenChange(false);
				openModal('passkeys');
			},
		});

		// API Keys - only if user has api_keys:manage permission
		if (user?.permissions.includes('api_keys:manage')) {
			cmds.push({
				id: 'settings-api-keys',
				label: 'API Keys',
				group: 'settings',
				icon: <Key className="size-4" />,
				action: () => {
					onOpenChange(false);
					openModal('api-keys');
				},
			});
		}

		// User Management - only if user has users:manage permission
		if (user?.permissions.includes('users:manage')) {
			cmds.push({
				id: 'settings-users',
				label: 'User Management',
				group: 'settings',
				icon: <Users className="size-4" />,
				action: () => {
					onOpenChange(false);
					openModal('users');
				},
			});

			// Enrichment Status - admin only
			cmds.push({
				id: 'settings-enrichment',
				label: 'Enrichment Status',
				group: 'settings',
				icon: <Activity className="size-4" />,
				action: () => {
					onOpenChange(false);
					openModal('enrichment');
				},
			});

			// Reddit Import Status - admin only
			cmds.push({
				id: 'settings-reddit-import',
				label: 'Reddit Import Status',
				group: 'settings',
				icon: <Activity className="size-4" />,
				action: () => {
					onOpenChange(false);
					openModal('reddit-import');
				},
			});

			// Tag Admin - admin only
			cmds.push({
				id: 'settings-tag-admin',
				label: 'Tag Admin',
				group: 'settings',
				icon: <Tag className="size-4" />,
				action: () => {
					onOpenChange(false);
					openModal('tag-admin');
				},
			});
		}

		// Filter Rules
		cmds.push({
			id: 'settings-filter-rules',
			label: 'Filter Rules',
			group: 'settings',
			icon: <Filter className="size-4" />,
			action: () => {
				onOpenChange(false);
				document.dispatchEvent(new CustomEvent('open-filter-rules'));
			},
		});

		// General Settings
		cmds.push({
			id: 'settings-general',
			label: 'Settings',
			group: 'settings',
			icon: <Settings className="size-4" />,
			action: () => {
				onOpenChange(false);
				openModal('settings');
			},
		});

		return cmds;
	}, [
		queues,
		selectedLink,
		showNsfw,
		user,
		openModal,
		onNavigateToQueue,
		onOpenChange,
		onToggleNsfw,
		onArchiveSelected,
		onOpenQuickAdd,
		onOpenCategorySelector,
		onOpenOriginalUrl,
		onEnrichSelected,
		onShowShortcuts,
	]);

	// Filter commands by query
	const filteredCommands = useMemo(() => {
		if (!query.trim()) return commands.filter((c) => !c.disabled);
		return commands.filter((c) => !c.disabled && fuzzyMatch(query, c.label));
	}, [commands, query]);

	// Group filtered commands
	const groupedCommands = useMemo(() => {
		const groups: { label: string; commands: Command[] }[] = [
			{ label: 'Navigation', commands: [] },
			{ label: 'Actions', commands: [] },
			{ label: 'View', commands: [] },
			{ label: 'Settings', commands: [] },
		];
		for (const cmd of filteredCommands) {
			const groupIndex =
				cmd.group === 'navigation' ? 0 : cmd.group === 'actions' ? 1 : cmd.group === 'view' ? 2 : 3;
			groups[groupIndex].commands.push(cmd);
		}
		return groups.filter((g) => g.commands.length > 0);
	}, [filteredCommands]);

	// Flat list for keyboard navigation
	const flatCommands = useMemo(() => groupedCommands.flatMap((g) => g.commands), [groupedCommands]);

	// Reset state when dialog opens
	useEffect(() => {
		if (open) {
			setQuery('');
			setSelectedIndex(0);
			setTimeout(() => inputRef.current?.focus(), 0);
		}
	}, [open]);

	// Reset selected index when filtered results change
	useEffect(() => {
		setSelectedIndex(0);
	}, []);

	// Scroll selected item into view when index changes
	useEffect(() => {
		if (selectedRef.current) {
			selectedRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
			});
		}
	}, [selectedIndex]);

	// Keyboard navigation
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					setSelectedIndex((prev) => Math.min(prev + 1, flatCommands.length - 1));
					break;
				case 'ArrowUp':
					e.preventDefault();
					setSelectedIndex((prev) => Math.max(prev - 1, 0));
					break;
				case 'Enter':
					e.preventDefault();
					if (flatCommands[selectedIndex]) {
						flatCommands[selectedIndex].action();
					}
					break;
				case 'Escape':
					onOpenChange(false);
					break;
			}
		},
		[flatCommands, selectedIndex, onOpenChange]
	);

	// Track global index for flat list rendering
	let globalIndex = -1;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton={false}
				className="top-[20%] translate-y-0 p-0 gap-0 overflow-hidden max-w-lg"
			>
				{/* Search Input */}
				<div className="flex items-center border-b border-border px-3">
					<span className="text-primary font-mono shrink-0">&gt;</span>
					<input
						ref={inputRef}
						type="text"
						placeholder="_"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						onKeyDown={handleKeyDown}
						className="flex-1 h-12 px-3 text-sm bg-transparent outline-none placeholder:text-primary font-mono"
					/>
					<kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 border border-border bg-background px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100 sm:flex">
						ESC
					</kbd>
				</div>

				{/* Results */}
				<ScrollArea className="max-h-[400px]">
					{flatCommands.length === 0 && query.trim() && (
						<div className="py-6 text-center text-sm text-muted-foreground font-mono">// no commands found</div>
					)}
					{groupedCommands.length > 0 && (
						<div className="py-2">
							{groupedCommands.map((group) => (
								<div key={group.label}>
									<div className="px-3 py-1.5 text-xs font-mono text-muted-foreground uppercase tracking-wider">
										[{group.label}]
									</div>
									{group.commands.map((cmd) => {
										globalIndex++;
										const idx = globalIndex;
										return (
											<button
												key={cmd.id}
												ref={idx === selectedIndex ? selectedRef : null}
												type="button"
												onClick={cmd.action}
												className={cn(
													'w-full flex items-center gap-3 px-3 py-2 text-left transition-colors font-mono',
													idx === selectedIndex
														? 'bg-primary/10 text-primary border-l-2 border-primary'
														: 'hover:bg-accent'
												)}
											>
												<span className="text-muted-foreground">{cmd.icon}</span>
												<span className="flex-1 text-sm">{cmd.label}</span>
												{cmd.shortcut && (
													<kbd className="px-1.5 py-0.5 border border-border bg-background text-xs font-mono text-muted-foreground">
														{cmd.shortcut}
													</kbd>
												)}
												<ChevronRight className="size-4 text-muted-foreground opacity-50" />
											</button>
										);
									})}
								</div>
							))}
						</div>
					)}
				</ScrollArea>

				{/* Footer */}
				<div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs text-muted-foreground font-mono">
					<div className="flex items-center gap-2">
						<kbd className="px-1.5 py-0.5 border border-border bg-background">↑↓</kbd>
						<span>nav</span>
						<kbd className="px-1.5 py-0.5 border border-border bg-background">↵</kbd>
						<span>run</span>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
